/**
 * Client-side cache for dynamic menu / allowed paths per role.
 */
import secureLocalStorage from "react-secure-storage";

const TTL_MS = 5 * 60 * 1000;
const memory = new Map();
const listeners = new Set();
const inflight = new Map();
const unavailable = new Set();
const refreshing = new Set();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (_) {
      /* a listener must not break the others */
    }
  });
}

export function subscribeMenuCache(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True while this role's menu grant is being loaded. */
export function isMenuAccessPending(role) {
  const r = String(role ?? "");
  if (!r) return false;
  if (refreshing.has(r)) return true;
  return !getCachedMenu(r) && !unavailable.has(r);
}

const storageKey = (role) => `menu_access_${role}`;

export function getCachedMenu(role) {
  const r = String(role ?? "");
  const mem = memory.get(r);
  if (mem && Date.now() - mem.ts < TTL_MS) return mem.data;

  try {
    const raw = secureLocalStorage.getItem(storageKey(r));
    if (!raw) return null;
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed?.ts || Date.now() - parsed.ts > TTL_MS) return null;
    memory.set(r, { ts: parsed.ts, data: parsed.data });
    return parsed.data;
  } catch (_) {
    return null;
  }
}

export function setCachedMenu(role, data) {
  const r = String(role ?? "");
  const payload = { ts: Date.now(), data };
  memory.set(r, payload);
  unavailable.delete(r);
  try {
    secureLocalStorage.setItem(storageKey(r), JSON.stringify(payload));
  } catch (_) {
    /* ignore quota */
  }
  notify();
}

/**
 * Load this role's menu once. A refresh replaces a stale grant so a page
 * the role was just given does not stay on the 403 screen.
 */
export function loadMenuAccess(role, fetcher, { refresh = false } = {}) {
  const r = String(role ?? "");
  if (!r) return Promise.resolve(null);
  if (!refresh) {
    const cached = getCachedMenu(r);
    if (cached) return Promise.resolve(cached);
    if (unavailable.has(r)) return Promise.resolve(null);
  }
  if (inflight.has(r)) return inflight.get(r);

  refreshing.add(r);
  notify();

  const job = Promise.resolve()
    .then(() => fetcher())
    .then((data) => {
      if (data && typeof data === "object") {
        setCachedMenu(r, data);
      }
      return getCachedMenu(r);
    })
    .catch((error) => {
      if (!getCachedMenu(r)) {
        unavailable.add(r);
        notify();
      }
      throw error;
    })
    .finally(() => {
      inflight.delete(r);
      refreshing.delete(r);
      notify();
    });

  inflight.set(r, job);
  return job;
}

export function clearMenuCache(role) {
  if (role != null) {
    const r = String(role);
    memory.delete(r);
    try {
      secureLocalStorage.removeItem(storageKey(r));
    } catch (_) {}
    return;
  }
  memory.clear();
}

/**
 * A granted menu path also covers its own detail URLs
 * (/nutritionist-assessment-form → /nutritionist-assessment-form/135).
 * A path of "/" must not unlock the whole app.
 */
export function menuPathMatches(menuPath, pathname) {
  if (!menuPath || !pathname) return false;
  if (pathname === menuPath) return true;
  const base = String(menuPath).replace(/\/$/, "");
  if (!base || base === "/") return false;
  return String(pathname).startsWith(`${base}/`);
}

export function isCatalogPath(allLinks, pathname) {
  return (allLinks || []).some((path) => menuPathMatches(path, pathname));
}

/** Fitness assessment view/download pages stay open for any logged-in staff. */
export function isStaffAssessmentPath(pathname) {
  return /^\/(viewAssessment|viewAssessmentInfo|postAssessment|client-assessment)(\/|$)/.test(
    String(pathname || "")
  );
}

export function pathAllowed(allowedPaths, pathname) {
  if (!pathname) return false;
  if (isStaffAssessmentPath(pathname)) return true;
  const paths = allowedPaths || [];
  for (const path of paths) {
    if (menuPathMatches(path, pathname)) return true;
  }
  return false;
}

/** Convert API modules tree into shape AlignedMenuItems expects. */
export function toAlignedModules(apiModules = []) {
  const mapNode = (node) => {
    const kind = node.kind;
    if (kind === "module" || (!kind && (node.id || node.children))) {
      return {
        id: node.id || node.item_key || String(node.label || "module"),
        item_key: node.item_key,
        label: node.label,
        icon: node.icon,
        children: (node.children || []).map(mapNode),
      };
    }
    if (kind === "group") {
      return {
        kind: "group",
        item_key: node.item_key,
        label: node.label,
        children: (node.children || []).map(mapNode),
      };
    }
    if (kind === "label") {
      return { kind: "label", label: node.label };
    }
    if (kind === "stub") {
      return { label: node.label, enabled: false };
    }
    // link
    return {
      label: node.label,
      path: node.path,
      enabled: node.enabled !== false,
      openInNewTab: !!node.openInNewTab,
    };
  };

  return (apiModules || []).map(mapNode);
}
